import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useLaunchState } from './LaunchContext';
import type { SessionRecord, SessionRole } from '@/lib/types';
import { readJsonStorage, removeStorage, storageKeys, writeJsonStorage } from '@/lib/storage';
import type { StaffMember } from '@/lib/types';

type SessionContextValue = {
  isReady: boolean;
  sessionToken: string | null;
  sessionRole: SessionRole | null;
  sessionName: string | null;
  sessionUsername: string | null;
  sessionStaffId: StaffMember['id'] | null;
  isAuthenticated: boolean;
  isEnterprise: boolean;
  isStaff: boolean;
  signIn: (session: {
    token: string;
    role?: SessionRole;
    name: string;
    username?: string | null;
    staffId?: StaffMember['id'] | null;
  }) => Promise<void>;
  signOut: () => Promise<void>;
};

const SessionContext = createContext<SessionContextValue | null>(null);

function isStaffMemberId(value: unknown): value is StaffMember['id'] {
  return typeof value === 'string' && value.trim().length > 0;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const { setWelcomeSeen } = useLaunchState();
  const [sessionToken, setSessionTokenState] = useState<string | null>(null);
  const [sessionRole, setSessionRoleState] = useState<SessionRole | null>(null);
  const [sessionName, setSessionNameState] = useState<string | null>(null);
  const [sessionUsername, setSessionUsernameState] = useState<string | null>(null);
  const [sessionStaffId, setSessionStaffIdState] = useState<StaffMember['id'] | null>(null);
  const [isReady, setIsReady] = useState(false);
  const isSigningOut = useRef(false);

  const signOut = useCallback(async () => {
    if (isSigningOut.current) {
      return;
    }

    isSigningOut.current = true;

    setSessionTokenState(null);
    setSessionRoleState(null);
    setSessionNameState(null);
    setSessionUsernameState(null);
    setSessionStaffIdState(null);

    removeStorage(storageKeys.session);

    isSigningOut.current = false;
  }, []);

  const signIn = useCallback(
    async ({
      token,
      role = 'enterprise',
      name,
      username = null,
      staffId = null,
    }: {
      token: string;
      role?: SessionRole;
      name: string;
      username?: string | null;
      staffId?: StaffMember['id'] | null;
    }) => {
      const nextRecord: SessionRecord = {
        token,
        role,
        name,
        username,
        staffId,
      };

      setSessionTokenState(token);
      setSessionRoleState(role);
      setSessionNameState(name);
      setSessionUsernameState(username);
      setSessionStaffIdState(staffId);
      writeJsonStorage(storageKeys.session, nextRecord);

      try {
        await setWelcomeSeen(true);
      } catch {
        // In-memory state still unlocks the app.
      }
    },
    [setWelcomeSeen]
  );

  useEffect(() => {
    let active = true;
    const stored = readJsonStorage<SessionRecord>(storageKeys.session);

    if (!active) {
      return;
    }

    if (stored && typeof stored.token === 'string') {
      setSessionTokenState(stored.token);
      setSessionRoleState(stored.role === 'staff' ? 'staff' : 'enterprise');
      setSessionNameState(typeof stored.name === 'string' && stored.name.trim() ? stored.name : 'Admin');
      setSessionUsernameState(
        typeof stored.username === 'string' && stored.username.trim() ? stored.username : null
      );
      setSessionStaffIdState(isStaffMemberId(stored.staffId) ? stored.staffId : null);
    } else {
      setSessionTokenState(null);
      setSessionRoleState(null);
      setSessionNameState(null);
      setSessionUsernameState(null);
      setSessionStaffIdState(null);
    }

    setIsReady(true);

    return () => {
      active = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      isReady,
      sessionToken,
      sessionRole,
      sessionName,
      sessionUsername,
      sessionStaffId,
      isAuthenticated: Boolean(sessionToken),
      isEnterprise: sessionRole === 'enterprise',
      isStaff: sessionRole === 'staff',
      signIn,
      signOut,
    }),
    [isReady, sessionName, sessionRole, sessionStaffId, sessionToken, sessionUsername, signIn, signOut]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSessionState() {
  const context = useContext(SessionContext);

  if (!context) {
    throw new Error('useSessionState must be used within a SessionProvider');
  }

  return context;
}
